import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { 
  Search, PawPrint, ArrowLeft, AlertTriangle, Phone, Mail, 
  Building2, Stethoscope, Calendar, MapPin, Shield, Heart,
  Cpu, User, Clock, AlertCircle, CheckCircle, Camera, Bell, Send, Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const MicrochipSearch = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Alert owner dialog state
  const [showAlertDialog, setShowAlertDialog] = useState(false);
  const [alerting, setAlerting] = useState(false);
  const [alertForm, setAlertForm] = useState({
    searcher_name: '',
    searcher_email: '',
    searcher_phone: '',
    message: ''
  });

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    
    setLoading(true);
    try {
      const response = await axios.get(`/microchips/search/${searchQuery}`);
      setResults(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Microchip not found');
      setResults(null);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    if (searchParams.get('q')) {
      handleSearch();
    }
  }, []);

  // Determine the display owner - rescue takes priority
  const getDisplayOwner = () => {
    if (results?.rescue) {
      return {
        type: 'rescue',
        name: results.rescue.organization_name || results.rescue.name,
        email: results.rescue.email,
        phone: results.rescue.phone,
        isRescue: true
      };
    }
    if (results?.owner) {
      return {
        type: 'owner',
        name: results.owner.name,
        email: results.owner.email,
        phone: results.owner.phone,
        isRescue: false,
        alert_on_search: results.owner.alert_on_search,
        require_contact_for_alert: results.owner.require_contact_for_alert,
        any_info_visible: results.owner.any_info_visible
      };
    }
    return null;
  };

  const handleAlertOwner = async () => {
    // Validate if owner requires contact info
    if (results?.owner?.require_contact_for_alert) {
      if (!alertForm.searcher_name || (!alertForm.searcher_email && !alertForm.searcher_phone)) {
        toast.error('Please provide your name and contact information');
        return;
      }
    }
    
    setAlerting(true);
    try {
      await axios.post('/microchips/alert-owner', {
        microchip_number: searchQuery,
        ...alertForm
      });
      toast.success('Owner has been notified! They will receive your contact information.');
      setShowAlertDialog(false);
      setAlertForm({ searcher_name: '', searcher_email: '', searcher_phone: '', message: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to alert owner');
    } finally {
      setAlerting(false);
    }
  };

  const displayOwner = results ? getDisplayOwner() : null;

  return (
    <div className="min-h-screen bg-[#fdfbf7]">
      <header className="bg-white border-b border-stone-100 py-4">
        <div className="container mx-auto px-6 flex items-center gap-4">
          <Button data-testid="back-btn" variant="ghost" onClick={() => navigate('/')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-2">
            <PawPrint className="w-6 h-6 text-primary" />
            <span className="text-xl font-bold">Microchip Search</span>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-12">
        <div data-testid="search-section" className="max-w-2xl mx-auto mb-12">
          <h1 className="text-4xl font-bold mb-6 text-center" style={{ fontFamily: 'Manrope, sans-serif' }}>Find a Pet by Microchip</h1>
          
          <div className="flex gap-2">
            <Input
              data-testid="chip-search-input"
              type="text"
              placeholder="Enter microchip number..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              className="h-12 text-lg"
            />
            <Button data-testid="search-chip-btn" onClick={handleSearch} disabled={loading} className="bg-primary hover:bg-primary/90 h-12 px-8">
              <Search className="w-5 h-5 mr-2" />
              {loading ? 'Searching...' : 'Search'}
            </Button>
          </div>
        </div>

        {results && (
          <div data-testid="search-results" className="max-w-5xl mx-auto">
            
            {/* MISSING PET ALERT - Top Banner */}
            {results.lost_report && (
              <div className="mb-6 p-4 bg-red-100 border-2 border-red-500 rounded-xl animate-pulse">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="w-8 h-8 text-red-600" />
                  <div>
                    <h2 className="text-xl font-bold text-red-700">⚠️ THIS PET IS REPORTED MISSING!</h2>
                    <p className="text-red-600">
                      Reported missing on: <strong>{
                        (() => {
                          const dateStr = results.lost_report.reported_date || results.lost_report.reported_at || results.lost_report.last_seen_date;
                          if (!dateStr) return 'Unknown date';
                          try {
                            return new Date(dateStr).toLocaleDateString('en-US', { 
                              weekday: 'long', 
                              year: 'numeric', 
                              month: 'long', 
                              day: 'numeric' 
                            });
                          } catch {
                            return dateStr;
                          }
                        })()
                      }</strong>
                    </p>
                    {results.lost_report.last_seen_location && (
                      <p className="text-red-600 flex items-center gap-1">
                        <MapPin className="w-4 h-4" /> Last seen: {results.lost_report.last_seen_location}
                      </p>
                    )}
                    <p className="text-red-700 mt-2 font-semibold">
                      If you have found this pet, please contact the owner immediately!
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* MEDICAL ALERTS */}
            {(results.pet?.medical_alerts || results.pet?.special_needs || results.pet?.health_status === 'critical') && (
              <div className="mb-6 p-4 bg-yellow-50 border-2 border-yellow-400 rounded-xl">
                <div className="flex items-center gap-3">
                  <AlertCircle className="w-6 h-6 text-yellow-600" />
                  <div>
                    <h3 className="font-bold text-yellow-800">Medical Alert</h3>
                    {results.pet.medical_alerts && (
                      <p className="text-yellow-700">{results.pet.medical_alerts}</p>
                    )}
                    {results.pet.special_needs && (
                      <p className="text-yellow-700">Special Needs: {results.pet.special_needs}</p>
                    )}
                    {results.pet.health_status === 'critical' && (
                      <Badge className="bg-red-500 text-white mt-1">Critical Health Status</Badge>
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="grid md:grid-cols-3 gap-6">
              
              {/* PET PHOTO & BASIC INFO - Large Left Card */}
              <Card className="md:col-span-1 md:row-span-2">
                <CardContent className="p-4">
                  {/* Pet Photo */}
                  <div className="aspect-square rounded-xl bg-stone-100 mb-4 overflow-hidden flex items-center justify-center">
                    {results.pet?.photos && results.pet.photos.length > 0 ? (
                      <img 
                        src={results.pet.photos[0]} 
                        alt={results.pet?.name || 'Pet'} 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="text-center text-stone-400">
                        <Camera className="w-16 h-16 mx-auto mb-2" />
                        <p>No Photo Available</p>
                      </div>
                    )}
                  </div>
                  
                  {/* Pet Name & Status */}
                  <div className="text-center">
                    <h2 className="text-2xl font-bold">{results.pet?.name || 'Unknown'}</h2>
                    <p className="text-stone-600">{results.pet?.species} • {results.pet?.breed || 'Unknown breed'}</p>
                    
                    <div className="flex flex-wrap gap-2 justify-center mt-3">
                      {results.pet?.gender && (
                        <Badge variant="outline">{results.pet.gender}</Badge>
                      )}
                      {results.pet?.color && (
                        <Badge variant="outline">{results.pet.color}</Badge>
                      )}
                      {results.pet?.spay_neuter_status && (
                        <Badge className="bg-green-100 text-green-700">{results.pet.spay_neuter_status}</Badge>
                      )}
                    </div>

                    {results.pet?.date_of_birth && (
                      <p className="text-sm text-stone-500 mt-3">
                        <Calendar className="w-4 h-4 inline mr-1" />
                        Born: {new Date(results.pet.date_of_birth).toLocaleDateString()}
                      </p>
                    )}
                  </div>

                  {/* Additional Photos */}
                  {results.pet?.photos && results.pet.photos.length > 1 && (
                    <div className="mt-4">
                      <p className="text-xs text-stone-500 mb-2">More Photos:</p>
                      <div className="grid grid-cols-3 gap-2">
                        {results.pet.photos.slice(1, 4).map((photo, idx) => (
                          <img 
                            key={idx}
                            src={photo} 
                            alt={`${results.pet.name} photo ${idx + 2}`}
                            className="aspect-square rounded object-cover"
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* OWNER/RESCUE INFORMATION - Priority to Rescue */}
              <Card className="md:col-span-2">
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2">
                    {displayOwner?.isRescue ? (
                      <>
                        <Building2 className="w-5 h-5 text-primary" />
                        Rescue/Shelter Contact
                      </>
                    ) : (
                      <>
                        <User className="w-5 h-5 text-primary" />
                        Owner Contact
                      </>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {displayOwner ? (
                    <div className="space-y-3">
                      <div className="flex items-center gap-3 p-3 bg-stone-50 rounded-lg">
                        {displayOwner.isRescue ? (
                          <Building2 className="w-10 h-10 text-primary" />
                        ) : (
                          <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                            <User className="w-6 h-6 text-primary" />
                          </div>
                        )}
                        <div>
                          <p className="font-bold text-lg">{displayOwner.name}</p>
                          {displayOwner.isRescue && (
                            <Badge className="bg-blue-100 text-blue-700">Animal Rescue/Shelter</Badge>
                          )}
                        </div>
                      </div>
                      
                      {displayOwner.email && (
                        <a href={`mailto:${displayOwner.email}`} className="flex items-center gap-2 p-3 bg-green-50 rounded-lg hover:bg-green-100 transition-colors">
                          <Mail className="w-5 h-5 text-green-600" />
                          <span className="text-green-700">{displayOwner.email}</span>
                        </a>
                      )}
                      
                      {displayOwner.phone && (
                        <a href={`tel:${displayOwner.phone}`} className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors">
                          <Phone className="w-5 h-5 text-blue-600" />
                          <span className="text-blue-700">{displayOwner.phone}</span>
                        </a>
                      )}
                      
                      {displayOwner.contact_available && !displayOwner.name && (
                        <p className="text-stone-600 italic">Owner prefers to be contacted via email/text. Click above to reach out.</p>
                      )}
                      
                      {/* Alert Owner Button - Only for non-rescue owners who have alerts enabled */}
                      {!displayOwner.isRescue && displayOwner.alert_on_search !== false && (
                        <Button 
                          onClick={() => setShowAlertDialog(true)}
                          className="w-full bg-amber-500 hover:bg-amber-600 text-white mt-2"
                        >
                          <Bell className="w-4 h-4 mr-2" />
                          Alert Owner (Found This Pet?)
                        </Button>
                      )}
                    </div>
                  ) : (
                    <p className="text-stone-500">No owner information available</p>
                  )}

                  {/* If there's BOTH a rescue AND an owner, show both */}
                  {results.rescue && results.owner && (
                    <div className="mt-4 pt-4 border-t border-stone-200">
                      <p className="text-sm text-stone-500 mb-2">Original Owner (if different):</p>
                      <div className="flex items-center gap-2 text-sm">
                        <User className="w-4 h-4 text-stone-400" />
                        <span>{results.owner.name}</span>
                        {results.owner.email && <span className="text-stone-400">• {results.owner.email}</span>}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* MICROCHIP INFORMATION */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Cpu className="w-5 h-5 text-primary" />
                    Microchip Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-stone-500">Chip Number:</span>
                    <span className="font-mono font-bold">{results.microchip.microchip_number}</span>
                  </div>
                  
                  {results.microchip.implant_date && (
                    <div className="flex justify-between">
                      <span className="text-stone-500">Implant Date:</span>
                      <span>{new Date(results.microchip.implant_date).toLocaleDateString()}</span>
                    </div>
                  )}
                  
                  <div className="flex justify-between items-center">
                    <span className="text-stone-500">Status:</span>
                    <Badge className={
                      results.microchip.registration_status === 'registered' 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-yellow-100 text-yellow-700'
                    }>
                      <CheckCircle className="w-3 h-3 mr-1" />
                      {results.microchip.registration_status || 'Registered'}
                    </Badge>
                  </div>

                  {results.microchip.chip_manufacturer && (
                    <div className="flex justify-between">
                      <span className="text-stone-500">Manufacturer:</span>
                      <span>{results.microchip.chip_manufacturer}</span>
                    </div>
                  )}

                  {results.microchip.secondary_microchip && (
                    <div className="flex justify-between">
                      <span className="text-stone-500">Secondary Chip:</span>
                      <span className="font-mono text-sm">{results.microchip.secondary_microchip}</span>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* VET/IMPLANT INFORMATION */}
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Stethoscope className="w-5 h-5 text-primary" />
                    Veterinary Information
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {results.vet ? (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 p-2 bg-stone-50 rounded">
                        <Stethoscope className="w-5 h-5 text-stone-500" />
                        <div>
                          <p className="font-medium">{results.vet.organization_name || results.vet.name}</p>
                          <p className="text-xs text-stone-500">Implanting Veterinarian</p>
                        </div>
                      </div>
                      {results.vet.phone && (
                        <p className="text-sm flex items-center gap-1">
                          <Phone className="w-3 h-3" /> {results.vet.phone}
                        </p>
                      )}
                      {results.vet.email && (
                        <p className="text-sm flex items-center gap-1">
                          <Mail className="w-3 h-3" /> {results.vet.email}
                        </p>
                      )}
                    </div>
                  ) : results.microchip.implanting_vet ? (
                    <div className="space-y-2">
                      <p className="font-medium">{results.microchip.implanting_vet}</p>
                      {results.microchip.implanting_clinic && (
                        <p className="text-sm text-stone-500">{results.microchip.implanting_clinic}</p>
                      )}
                    </div>
                  ) : (
                    <p className="text-stone-500 text-sm">No veterinary information on file</p>
                  )}
                </CardContent>
              </Card>

              {/* RESCUE/SHELTER ATTACHED TO PET */}
              {results.rescue && (
                <Card className="md:col-span-2">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Building2 className="w-5 h-5 text-primary" />
                      Rescue/Shelter of Origin
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-start gap-4">
                      <div className="w-16 h-16 bg-primary/10 rounded-lg flex items-center justify-center">
                        <Building2 className="w-8 h-8 text-primary" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-bold text-lg">{results.rescue.organization_name || results.rescue.name}</h3>
                        <Badge className="bg-green-100 text-green-700 mt-1">
                          <Shield className="w-3 h-3 mr-1" /> Verified Rescue
                        </Badge>
                        <div className="grid grid-cols-2 gap-2 mt-3 text-sm">
                          {results.rescue.email && (
                            <a href={`mailto:${results.rescue.email}`} className="flex items-center gap-1 text-primary hover:underline">
                              <Mail className="w-4 h-4" /> {results.rescue.email}
                            </a>
                          )}
                          {results.rescue.phone && (
                            <a href={`tel:${results.rescue.phone}`} className="flex items-center gap-1 text-primary hover:underline">
                              <Phone className="w-4 h-4" /> {results.rescue.phone}
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* IMPLANTER INFORMATION */}
              {results.implanter && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Stethoscope className="w-5 h-5 text-green-600" />
                      Chip Implanter
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {results.implanter.name && (
                        <p className="font-medium">{results.implanter.name}</p>
                      )}
                      {results.implanter.clinic && (
                        <p className="text-sm text-stone-600">{results.implanter.clinic}</p>
                      )}
                      {results.implanter.organization_name && (
                        <p className="text-sm text-stone-600">{results.implanter.organization_name}</p>
                      )}
                      {results.implanter.implant_date && (
                        <p className="text-xs text-stone-500">
                          <Calendar className="w-3 h-3 inline mr-1" />
                          Implanted: {results.implanter.implant_date}
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* BREEDER INFORMATION */}
              {results.breeder && (
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Heart className="w-5 h-5 text-pink-500" />
                      Breeder
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <p className="font-medium">{results.breeder.organization_name || results.breeder.name}</p>
                      {results.breeder.city && results.breeder.state && (
                        <p className="text-sm text-stone-500 flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {results.breeder.city}, {results.breeder.state}
                        </p>
                      )}
                      {results.breeder.phone && (
                        <p className="text-sm flex items-center gap-1">
                          <Phone className="w-3 h-3" /> {results.breeder.phone}
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* FAMILY CONNECTIONS - Dam, Sire, Siblings */}
              {results.family && (results.family.dam || results.family.sire || results.family.siblings?.length > 0) && (
                <Card className="md:col-span-3">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <PawPrint className="w-5 h-5 text-primary" />
                      Family Connections
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid md:grid-cols-3 gap-4">
                      {/* Dam (Mother) */}
                      {results.family.dam && (
                        <div className="p-3 bg-pink-50 rounded-lg border border-pink-100">
                          <p className="text-xs text-pink-600 font-medium mb-1">Dam (Mother)</p>
                          <div className="flex items-center gap-2">
                            {results.family.dam.photos?.[0] ? (
                              <img 
                                src={results.family.dam.photos[0]} 
                                alt={results.family.dam.name}
                                className="w-10 h-10 rounded-full object-cover"
                              />
                            ) : (
                              <div className="w-10 h-10 bg-pink-200 rounded-full flex items-center justify-center">
                                <PawPrint className="w-5 h-5 text-pink-500" />
                              </div>
                            )}
                            <div>
                              <p className="font-medium">{results.family.dam.name}</p>
                              {results.family.dam.breed && (
                                <p className="text-xs text-stone-500">{results.family.dam.breed}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Sire (Father) */}
                      {results.family.sire && (
                        <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                          <p className="text-xs text-blue-600 font-medium mb-1">Sire (Father)</p>
                          <div className="flex items-center gap-2">
                            {results.family.sire.photos?.[0] ? (
                              <img 
                                src={results.family.sire.photos[0]} 
                                alt={results.family.sire.name}
                                className="w-10 h-10 rounded-full object-cover"
                              />
                            ) : (
                              <div className="w-10 h-10 bg-blue-200 rounded-full flex items-center justify-center">
                                <PawPrint className="w-5 h-5 text-blue-500" />
                              </div>
                            )}
                            <div>
                              <p className="font-medium">{results.family.sire.name}</p>
                              {results.family.sire.breed && (
                                <p className="text-xs text-stone-500">{results.family.sire.breed}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Litter Info */}
                      {results.family.litter && (
                        <div className="p-3 bg-amber-50 rounded-lg border border-amber-100">
                          <p className="text-xs text-amber-600 font-medium mb-1">Litter</p>
                          <p className="font-medium">{results.family.litter.litter_name}</p>
                          {results.family.litter.birth_date && (
                            <p className="text-xs text-stone-500">Born: {results.family.litter.birth_date}</p>
                          )}
                          {results.family.litter.breeder_name && (
                            <p className="text-xs text-stone-500">Breeder: {results.family.litter.breeder_name}</p>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Siblings */}
                    {results.family.siblings?.length > 0 && (
                      <div className="mt-4">
                        <p className="text-sm font-medium text-stone-600 mb-2">
                          Siblings ({results.family.siblings.length})
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {results.family.siblings.map((sibling, idx) => (
                            <div 
                              key={sibling.animal_id || idx}
                              className="flex items-center gap-2 p-2 bg-stone-50 rounded-lg border"
                            >
                              {sibling.photos?.[0] ? (
                                <img 
                                  src={sibling.photos[0]} 
                                  alt={sibling.name}
                                  className="w-8 h-8 rounded-full object-cover"
                                />
                              ) : (
                                <div className="w-8 h-8 bg-stone-200 rounded-full flex items-center justify-center">
                                  <PawPrint className="w-4 h-4 text-stone-400" />
                                </div>
                              )}
                              <div>
                                <p className="text-sm font-medium">{sibling.name}</p>
                                <p className="text-xs text-stone-500">
                                  {sibling.gender} {sibling.status && `• ${sibling.status}`}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* TRANSFER HISTORY */}
              {results.history && results.history.length > 0 && (
                <Card className="md:col-span-3">
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center gap-2 text-lg">
                      <Clock className="w-5 h-5 text-primary" />
                      Ownership History
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {results.history.map((entry, idx) => (
                        <div key={idx} className="flex items-center gap-3 p-2 bg-stone-50 rounded text-sm">
                          <div className="w-8 h-8 bg-stone-200 rounded-full flex items-center justify-center text-xs font-bold">
                            {idx + 1}
                          </div>
                          <div className="flex-1">
                            <p className="font-medium">{entry.action || entry.type}</p>
                            <p className="text-stone-500 text-xs">
                              {entry.date && new Date(entry.date).toLocaleDateString()}
                              {entry.from && ` • From: ${entry.from}`}
                              {entry.to && ` • To: ${entry.to}`}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

            </div>
          </div>
        )}
      </div>

      {/* Alert Owner Dialog */}
      <Dialog open={showAlertDialog} onOpenChange={setShowAlertDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-amber-500" />
              Alert Pet Owner
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-stone-600">
              The pet owner will be notified that their microchip was searched. 
              {results?.owner?.require_contact_for_alert && (
                <span className="text-red-600 font-medium"> Your contact information is required.</span>
              )}
            </p>

            <div>
              <Label>Your Name {results?.owner?.require_contact_for_alert && <span className="text-red-500">*</span>}</Label>
              <Input
                value={alertForm.searcher_name}
                onChange={(e) => setAlertForm({ ...alertForm, searcher_name: e.target.value })}
                placeholder="Enter your name"
              />
            </div>

            <div>
              <Label>Your Email {results?.owner?.require_contact_for_alert && <span className="text-red-500">*</span>}</Label>
              <Input
                type="email"
                value={alertForm.searcher_email}
                onChange={(e) => setAlertForm({ ...alertForm, searcher_email: e.target.value })}
                placeholder="Enter your email"
              />
            </div>

            <div>
              <Label>Your Phone</Label>
              <Input
                type="tel"
                value={alertForm.searcher_phone}
                onChange={(e) => setAlertForm({ ...alertForm, searcher_phone: e.target.value })}
                placeholder="Enter your phone number"
              />
            </div>

            <div>
              <Label>Message to Owner</Label>
              <Textarea
                value={alertForm.message}
                onChange={(e) => setAlertForm({ ...alertForm, message: e.target.value })}
                placeholder="I found your pet at... / I'm interested in this pet because..."
                rows={3}
              />
            </div>

            <div className="p-3 bg-amber-50 rounded-lg text-sm">
              <p className="text-amber-800">
                <strong>Note:</strong> Your contact information will be shared with the pet owner so they can reach out to you.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAlertDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleAlertOwner} 
              disabled={alerting}
              className="bg-amber-500 hover:bg-amber-600"
            >
              {alerting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" /> Send Alert
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MicrochipSearch;
